import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, ExternalLink, Loader2, Smartphone } from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const AZAANCE_URL = "https://www.azaanceapp.store";
export const AZAANCE_LOGO = `${AZAANCE_URL}/assets/azaance-logo-BAm7qNwE.jpg`;

const copy = (text, msg) => navigator.clipboard.writeText(text).then(() => toast.success(msg));

// Adds the FSC network (native FMI) then every listed token; each step is approved inside Azaance
const setupFscInWallet = async (addFSCNetwork, addToken) => {
  toast.info("Approve the FSC network and tokens in Azaance");
  if (!(await addFSCNetwork())) return;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tokenlist.json`);
    const { tokens = [] } = await res.json();
    const added = [];
    for (const t of tokens) {
      if (await addToken(t.address, t.decimals, t.symbol, t.logoURI)) added.push(t.symbol);
    }
    if (added.length) toast.success(`Added FMI, ${added.join(", ")} to Azaance`);
  } catch {
    toast.error("Could not add FSC tokens automatically");
  }
};

const PairingQr = ({ uri }) => (
  <div className="flex flex-col items-center gap-3">
    <div data-testid="azaance-qr" className="bg-white p-3 rounded-xl">
      {uri ? <QRCodeSVG value={uri} size={196} /> : <div className="w-[196px] h-[196px] grid place-items-center"><Loader2 className="animate-spin text-zinc-400" /></div>}
    </div>
    <Button data-testid="azaance-copy-link-btn" variant="outline" disabled={!uri}
      onClick={() => { copy(uri, "Link copied — paste it in Azaance and approve"); window.open(`${AZAANCE_URL}/connect`, "_blank", "noopener"); }}
      className="w-full border-[#2A2411] bg-transparent text-white hover:bg-[#D4AF37]/10 hover:text-white">
      <Copy size={14} className="mr-2" /> Copy link & open Azaance
    </Button>
  </div>
);

const OpenInsideAzaance = () => (
  <div className="border-t border-[#2A2411] pt-4 space-y-2">
    <p className="text-xs text-zinc-500">On your phone? Open FSC inside Azaance's dApp browser instead.</p>
    <Button data-testid="azaance-open-inside-btn" variant="ghost"
      onClick={() => { copy(window.location.href, "FSC link copied — paste it in Azaance's browser"); window.open(AZAANCE_URL, "_blank", "noopener"); }}
      className="w-full text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F3E5AB]">
      <Smartphone size={14} className="mr-2" /> Open FSC in Azaance <ExternalLink size={12} className="ml-1.5" />
    </Button>
  </div>
);

export const AzaanceConnectDialog = ({ open, onOpenChange }) => {
  const { connectWalletConnect, addFSCNetwork, addUSDTToken } = useWallet();
  const [uri, setUri] = useState("");
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setUri("");
    setStalled(false);
    const timer = setTimeout(() => setStalled(true), 15000);
    connectWalletConnect((u) => { clearTimeout(timer); setUri(u); }).then((addr) => {
      if (!addr) return;
      onOpenChange(false);
      setupFscInWallet(addFSCNetwork, addUSDTToken);
    });
    return () => clearTimeout(timer);
  }, [open, connectWalletConnect, onOpenChange, addFSCNetwork, addUSDTToken]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="azaance-connect-dialog" className="bg-[#0A0A0A] border-[#2A2411] text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src={AZAANCE_LOGO} alt="Azaance" className="w-6 h-6 rounded-md" /> Connect Azaance Wallet
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            In Azaance, open WalletConnect / "Connect dApp", then scan this code or paste the link. Approve the request in Azaance.
          </DialogDescription>
        </DialogHeader>
        <PairingQr uri={uri} />
        {stalled && !uri && (
          <p data-testid="azaance-connect-error" className="text-xs text-amber-400">
            WalletConnect is not responding for this website. The site's domain must be added to the allowed domains of the WalletConnect (Reown) project.
          </p>
        )}
        <OpenInsideAzaance />
      </DialogContent>
    </Dialog>
  );
};
